// src/hooks/useDialogA11y.js
// Shared accessibility behaviour for anything that overlays the page and
// expects the reader's attention: focus moves in when it opens, Tab cycles
// inside it, Escape closes it, and focus returns to wherever the reader was
// when it closes. WCAG 2.4.3 / 2.1.2 — every dialog in the app routes
// through this so the behaviour can't drift per-overlay.
import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * @param {boolean} open        whether the dialog is currently shown
 * @param {Function} onClose    called on Escape (omit for overlays that manage their own keys)
 * @returns ref — attach to the dialog's outermost focus boundary
 */
export default function useDialogA11y(open, onClose) {
  const ref = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const previouslyFocussed = document.activeElement;

    // Focus the dialog itself first so a screen reader announces its label
    // before the reader lands on the first control.
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
    node.focus({ preventScroll: true });

    const onKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = Array.from(node.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) {
        e.preventDefault();
        node.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !node.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !node.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      // The opener can be gone by close time (route change unmounted it).
      if (previouslyFocussed && previouslyFocussed.isConnected && previouslyFocussed.focus) {
        previouslyFocussed.focus({ preventScroll: true });
      }
    };
  }, [open]);

  return ref;
}
